import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Spin } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { Colors } from 'context';
import { CareerProgress, CareerStageCard } from 'molecules';
import { PATHS } from 'router/config';

import { CareerPreviewNotice } from './CareerPreviewNotice';
import { useCareerAccessGuard } from './useCareerAccessGuard';

/**
 * /career — the Career Center landing (spec 0018 §6).
 *
 * Greeting + direction + overall progress, then the three stage cards.
 * All three stages are live: Обучение → /career/learning, Резюме → /resume
 * (the profile/CV editor), Вакансии → /career/vacancies. Резюме used to render
 * as "coming soon" while the editor was already shipped at /resume — the card
 * contradicted the product, so it is now wired to it.
 */
export const CareerCenter = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { center, isPreview, error } = useCareerAccessGuard();

  if (error) {
    return <Alert type="error" showIcon message={t('careerCenter.loadError')} />;
  }

  if (!center) {
    // Either still loading, or a non-member the guard is about to bounce.
    return (
      <SpinWrapper>
        <Spin size="large" />
      </SpinWrapper>
    );
  }

  return (
    <PageWrapper>
      {isPreview ? <CareerPreviewNotice /> : null}
      <PageHeader>
        <PageTitle>{t('careerCenter.title')}</PageTitle>
        <PageInfo>{t('careerCenter.subtitle')}</PageInfo>
        {center.directionTitle ? (
          <DirectionBadge>
            {t('careerCenter.direction', { direction: center.directionTitle })}
          </DirectionBadge>
        ) : null}
      </PageHeader>

      <ProgressCard>
        <CareerProgress
          completed={center.lessonsCompleted}
          total={center.lessonsTotal}
          percent={center.progressPercent}
        />
      </ProgressCard>

      <StagesGrid>
        <CareerStageCard
          icon="🎓"
          title={t('careerCenter.stages.learning.title')}
          description={t('careerCenter.stages.learning.description')}
          meta={`${center.lessonsCompleted} / ${center.lessonsTotal}`}
          actionLabel={t('careerCenter.stages.learning.action')}
          onAction={() => navigate(PATHS.careerLearning)}
        />

        <CareerStageCard
          icon="📄"
          title={t('careerCenter.stages.resume.title')}
          description={t('careerCenter.stages.resume.description')}
          actionLabel={t('careerCenter.stages.resume.action')}
          onAction={() => navigate(PATHS.resume)}
        />

        <CareerStageCard
          icon="💼"
          title={t('careerCenter.stages.vacancies.title')}
          description={t('careerCenter.stages.vacancies.description')}
          actionLabel={t('careerCenter.stages.vacancies.action')}
          onAction={() => navigate(PATHS.careerVacancies)}
        />

        {/* ТЗ 0039 доводка: четвёртая плитка. Раздел «Мой рост» жил своей
            жизнью — из карьерного центра о нём нельзя было узнать вовсе, хотя
            именно участник центра ближе всех к следующей медали. */}
        <CareerStageCard
          icon="🏅"
          title={t('careerCenter.stages.growth.title')}
          description={t('careerCenter.stages.growth.description')}
          actionLabel={t('careerCenter.stages.growth.action')}
          onAction={() => navigate(PATHS.growth)}
        />
      </StagesGrid>
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const PageHeader = styled.div`
  margin: 32px 0;

  ${tablet} {
    margin: 40px 0 32px 0;
  }

  ${desktop} {
    margin: 54px 0 32px 0;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  ${tablet} {
    ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  }
`;

const DirectionBadge = styled.div`
  display: inline-block;
  margin-top: 12px;
  padding: 6px 14px;
  border-radius: 16px;
  background-color: ${Colors.bg_gray};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const ProgressCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 16px;
  background-color: ${Colors.white};
`;

const StagesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;

  ${tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;
